import { quoteTimestampMs } from './quoteMcap.js';
import { quoteAssetKind } from './quoteAssets.js';

export const CHANGE_FRESH_MS = 120000;
export const HISTORY_SAMPLE_MS = 5 * 60 * 1000;
export const HISTORY_TOLERANCE_MS = 10 * 60 * 1000;
export const HISTORY_RETENTION_MS = 25 * 60 * 60 * 1000;
export const HISTORY_MAX_TOKENS = 160;
export const HISTORY_MAX_POINTS = 301;
export const HISTORY_STORAGE_KEY = 'flap_monitor_price_history_v1';
const MAX_STORAGE_BYTES = 1500000;
const TOKEN = /^0x[a-f0-9]{40}$/;
const HOUR = 3600000;

function positive(value) {
  return Number.isFinite(Number(value)) && Number(value) > 0;
}

function emptyChange(status, hours, updatedAt = 0, coverage = 0) {
  return {
    pct: null, status, source: '', unit: 'USD', updatedAt, referenceAt: 0,
    toleranceMs: HISTORY_TOLERANCE_MS, coverage, hours,
  };
}

/** Only actual, timestamped USD observations enter the bounded browser history. */
export function createPriceHistory() {
  const entries = new Map();
  let dirty = false;

  function prune(now) {
    for (const [token, points] of entries) {
      const kept = points.filter(([at]) => now - at <= HISTORY_RETENTION_MS && at <= now + 5000);
      if (!kept.length) entries.delete(token);
      else if (kept.length !== points.length) entries.set(token, kept);
    }
    if (entries.size > HISTORY_MAX_TOKENS) {
      const oldest = [...entries].sort((a, b) => a[1].at(-1)[0] - b[1].at(-1)[0]);
      for (const [token] of oldest.slice(0, entries.size - HISTORY_MAX_TOKENS)) entries.delete(token);
    }
  }

  function observe(token, price, at, now = Date.now()) {
    token = String(token || '').toLowerCase();
    if (!TOKEN.test(token) || !positive(price) || !positive(at) ||
      at > now + 5000 || now - at >= CHANGE_FRESH_MS) return false;
    const points = entries.get(token) || [];
    const last = points.at(-1);
    // Repeated stale snapshots must not manufacture a price history.
    if (last && at - last[0] < HISTORY_SAMPLE_MS) return false;
    points.push([at, Number(price)]);
    entries.set(token, points.slice(-HISTORY_MAX_POINTS));
    prune(now);
    dirty = true;
    return true;
  }

  function change(token, price, at, hours, now = Date.now(), stale = false) {
    const duration = hours * HOUR;
    const points = entries.get(String(token || '').toLowerCase()) || [];
    const coverage = points.length && at > 0 ? Math.min(1, Math.max(0, (at - points[0][0]) / duration)) : 0;
    if (!positive(price) || !positive(at)) return emptyChange('missing', hours, at || 0, coverage);
    if (stale || now - at >= CHANGE_FRESH_MS || at > now + 5000) return emptyChange('stale', hours, at, coverage);
    const target = at - duration;
    let reference = null;
    for (const point of points) {
      if (Math.abs(point[0] - target) > HISTORY_TOLERANCE_MS || point[0] >= at) continue;
      if (!reference || Math.abs(point[0] - target) < Math.abs(reference[0] - target)) reference = point;
    }
    if (!reference) return emptyChange('pending', hours, at, coverage);
    const pct = (Number(price) / reference[1] - 1) * 100;
    if (!Number.isFinite(pct)) return emptyChange('missing', hours, at, coverage);
    return {
      ...emptyChange('ready', hours, at, coverage), pct, source: 'local-history',
      referenceAt: reference[0], referenceOffsetMs: reference[0] - target,
    };
  }

  function restore(raw, now = Date.now()) {
    try {
      if (typeof raw !== 'string' || raw.length > MAX_STORAGE_BYTES) return;
      const saved = JSON.parse(raw);
      if (saved?.version !== 1 || !Array.isArray(saved.entries)) return;
      for (const row of saved.entries.slice(-HISTORY_MAX_TOKENS)) {
        if (!Array.isArray(row) || row.length !== 2) continue;
        const [token, points] = row;
        if (!TOKEN.test(token) || !Array.isArray(points)) continue;
        let lastAt = 0;
        const clean = [];
        for (const point of points.slice(-HISTORY_MAX_POINTS)) {
          if (!Array.isArray(point) || !positive(point[0]) || !positive(point[1]) ||
            point[0] - lastAt < HISTORY_SAMPLE_MS || now - point[0] > HISTORY_RETENTION_MS || point[0] > now + 5000) continue;
          lastAt = Number(point[0]);
          clean.push([lastAt, Number(point[1])]);
        }
        if (clean.length) entries.set(token, clean);
      }
      prune(now);
    } catch { /* Corrupt or incompatible storage starts a new history. */ }
  }

  function serialize(now = Date.now()) {
    prune(now);
    const rows = [...entries].sort((a, b) => a[1].at(-1)[0] - b[1].at(-1)[0]);
    let raw = JSON.stringify({ version: 1, entries: rows });
    while (raw.length > MAX_STORAGE_BYTES && rows.length) {
      rows.shift();
      raw = JSON.stringify({ version: 1, entries: rows });
    }
    return raw;
  }

  return { observe, change, restore, serialize, has: (token) => entries.has(token),
    isDirty: () => dirty, markSaved: () => { dirty = false; } };
}

/** Explicit additive API fields only; never infer h4 from an upstream h6. */
export function upstream24h(q, now = Date.now()) {
  const raw = q?.change24hPct;
  const at = quoteTimestampMs(q?.change24hUpdatedAt);
  const createdAt = quoteTimestampMs(q?.change24hPairCreatedAt);
  if (raw == null || raw === '' || typeof raw === 'boolean' || !Number.isFinite(Number(raw)) ||
    Number(raw) < -100 || q?.change24hSource !== 'dexscreener' || q?.change24hUnit !== 'USD' ||
    !at || at > now + 5000 || !createdAt || at - createdAt < 24 * HOUR) return null;
  return {
    ...emptyChange(now - at < CHANGE_FRESH_MS ? 'ready' : 'stale', 24, at, 1),
    pct: now - at < CHANGE_FRESH_MS ? Number(raw) : null,
    source: 'dexscreener', referenceAt: at - 24 * HOUR, toleranceMs: 0,
  };
}

/** Dex percentages describe the base token. Stock quote / inverted pairs are excluded. */
export function dex24hQuotes(pairs, tokens, quotes = {}, now = Date.now()) {
  const wanted = new Set(tokens);
  const best = {};
  for (const pair of Array.isArray(pairs) ? pairs : []) {
    const token = String(pair?.baseToken?.address || '').toLowerCase();
    if (pair?.chainId !== 'bsc' || !wanted.has(token)) continue;
    const q = quotes[token];
    if (q?.pairUrl && String(q.pairUrl).toLowerCase() !== String(pair.url || '').toLowerCase()) continue;
    const quoteAddress = String(pair?.quoteToken?.address || '').toLowerCase();
    if (!TOKEN.test(quoteAddress) || !['bnb', 'stable'].includes(quoteAssetKind(quoteAddress))) continue;
    const createdAt = quoteTimestampMs(pair.pairCreatedAt);
    const raw = pair.priceChange?.h24;
    const price = Number(pair.priceUsd);
    const native = Number(pair.priceNative);
    const liquidity = Number(pair.liquidity?.usd);
    if (raw == null || raw === '' || typeof raw === 'boolean' || !Number.isFinite(Number(raw)) ||
      Number(raw) < -100 || !createdAt || now - createdAt < 24 * HOUR ||
      !positive(price) || !positive(native) || native >= 1000 ||
      !positive(liquidity) || Number(pair.fdv || 0) >= 1e12) continue;
    if (best[token] && best[token].liquidity >= liquidity) continue;
    best[token] = {
      change24hPct: Number(raw), change24hSource: 'dexscreener', change24hUnit: 'USD',
      change24hUpdatedAt: now, change24hPairCreatedAt: createdAt,
      priceUsd: price, updatedAt: now, liquidity,
    };
  }
  return best;
}
